/**
 * Barre de filtres du planning (SPEC PL7, PL8, PL10, P4, P5, P7) : Marque en étiquettes à sélection unique (une par
 * marque de la ville, vue multi-marques seulement ; à l'ouverture, la marque du site ; plus de « Toutes » depuis les
 * retours du 07/10), Studios et Coachs en listes déroulantes, Cours en étiquettes (types configurés, toggle du BO),
 * « Effacer » dès qu'un filtre est posé (la marque du site, par défaut, n'en est pas un). Pas d'interrupteur « Toutes
 * les marques » (P5).
 * Encart « groupe » (vue multi-marques, réglage « Encart groupe du planning » du BO) : la phrase du réglage, puis les
 * autres marques de la ville avec leur accroche et un lien vers leur site (retours de Ségolène du 13/10 : « on a switché
 * entre les 3 marques, mais rien ne dit ce qu'elles sont »). À droite des étiquettes Cours au bureau, dessous sur mobile.
 * Retours de Daphné du 15/10 (Dynamo) : étiquettes des autres marques en gris (data-current sur celle du site,
 * planning.css), « pour que dynamo ressorte mieux » ; étiquettes « Créneau » (Matin, Midi, Soir, comme EPISOD) à côté des
 * étiquettes Cours.
 */
import type { CSSProperties } from 'react';
import { brandSiteUrl, type PlanningBrand } from '../../lib/planning/brands';
import { TIME_SLOTS, type FilterOption, type FilterOptionGroup, type PlanningFilters } from '../../lib/planning/filters';
import { FilterSelect } from './FilterSelect';
import { IconClose, IconExternal } from './icons';

interface FiltersProps {
  filters: PlanningFilters;
  multiBrand: boolean;
  brands: PlanningBrand[];
  colorOf: (siteId: string) => string;
  studioGroups: FilterOptionGroup[];
  coachGroups: FilterOptionGroup[];
  /** Noms des studios et coachs choisis connus par les cours chargés (listes en chargement). */
  knownNames?: { studios: Map<string, string>; coaches: Map<string, string> };
  courseTypes: FilterOption[];
  /** Phrase de l'encart « groupe » ('' = pas d'encart). */
  groupText?: string;
  /** Mode coach : liste des coachs figée. */
  coachLocked: boolean;
  canClear: boolean;
  onBrand: (siteId: string) => void;
  onStudios: (keys: string[]) => void;
  onCoaches: (keys: string[]) => void;
  onToggleType: (key: string) => void;
  onToggleSlot: (slot: string) => void;
  /** `pointer` : « Effacer » activé à la souris ou au toucher (pas de contour de focus sur la cible). */
  onClear: (pointer: boolean) => void;
}

const dot = (color: string) => (
  <span className="mmv2-chip__dot" style={{ '--mmv2-dot': color } as CSSProperties} aria-hidden="true" />
);

export function Filters({
  filters,
  multiBrand,
  brands,
  colorOf,
  studioGroups,
  coachGroups,
  knownNames,
  courseTypes,
  groupText = '',
  coachLocked,
  canClear,
  onBrand,
  onStudios,
  onCoaches,
  onToggleType,
  onToggleSlot,
  onClear,
}: FiltersProps) {
  const typeDots = multiBrand && filters.brand === null;
  const others = brands.filter((brand) => !brand.isCurrent);
  const groupInfo =
    multiBrand && groupText && others.length > 0 ? (
      <p className="mmv2-filters__group-info">
        <span className="mmv2-filters__group-text">{groupText}</span>
        {others.map((brand) => {
          const href = brandSiteUrl(brand.url);
          const content = (
            <>
              {dot(brand.color)}
              <b className="mmv2-filters__group-name">{brand.name}</b>
              {brand.tagline ? <span className="mmv2-filters__group-tagline">{brand.tagline}</span> : null}
            </>
          );
          return href ? (
            <a key={brand.siteId} className="mmv2-filters__group-brand" href={href} target="_blank" rel="noopener noreferrer">
              {content}
              <IconExternal className="mmv2-filters__group-icon" />
              <span className="mmv2-sr-only"> (nouvel onglet)</span>
            </a>
          ) : (
            <span key={brand.siteId} className="mmv2-filters__group-brand">
              {content}
            </span>
          );
        })}
      </p>
    ) : null;

  return (
    <div className="mmv2-filters" role="group" aria-label="Filtres du planning">
      <div className="mmv2-filters__row">
        {multiBrand ? (
          <div className="mmv2-filters__group" role="group" aria-labelledby="mmv2-filter-brand">
            <span className="mmv2-filters__label" id="mmv2-filter-brand">
              Marque
            </span>
            <div className="mmv2-chips mmv2-filters__chips">
              {brands.map((brand) => (
                <button
                  key={brand.siteId}
                  type="button"
                  className="mmv2-chip mmv2-chip--brand"
                  data-brand={brand.siteId}
                  data-current={brand.isCurrent ? '' : undefined}
                  aria-pressed={filters.brand === brand.siteId}
                  onClick={() => onBrand(brand.siteId)}
                >
                  {dot(brand.color)}
                  {brand.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mmv2-filters__selects">
          <FilterSelect
            id="studios"
            label="Studios"
            allLabel="Tous les studios"
            manyLabel={(n) => `${n} studios`}
            labelFor={(key) => knownNames?.studios.get(key)}
            groups={studioGroups}
            grouped={multiBrand && filters.brand === null && studioGroups.length > 1}
            colorOf={colorOf}
            value={filters.studios}
            onChange={onStudios}
          />
          <FilterSelect
            id="coaches"
            label="Coachs"
            allLabel="Tous les coachs"
            manyLabel={(n) => `${n} coachs`}
            labelFor={(key) => knownNames?.coaches.get(key)}
            groups={coachGroups}
            grouped={multiBrand && filters.brand === null && coachGroups.length > 1}
            colorOf={colorOf}
            value={filters.coaches}
            onChange={onCoaches}
            isDisabled={coachLocked}
            menuAlign="end"
          />
          {canClear ? (
            <button
              type="button"
              className="mmv2-btn mmv2-btn--quiet mmv2-btn--sm mmv2-filters__clear"
              // detail > 0 : clic de souris ou toucher (0 au clavier, Entrée ou Espace).
              onClick={(event) => onClear(event.detail > 0)}
            >
              Effacer
              <IconClose />
            </button>
          ) : null}
        </div>
      </div>

      <div className="mmv2-filters__row">
        <div className="mmv2-filters__groups">
          {courseTypes.length > 0 ? (
            <div className="mmv2-filters__group" role="group" aria-labelledby="mmv2-filter-types">
              <span className="mmv2-filters__label" id="mmv2-filter-types">
                Cours
              </span>
              <div className="mmv2-chips mmv2-filters__chips">
                {courseTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className="mmv2-chip"
                    data-brand={type.siteId}
                    aria-pressed={filters.types.includes(type.value)}
                    onClick={() => onToggleType(type.value)}
                  >
                    {typeDots ? dot(colorOf(type.siteId)) : null}
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="mmv2-filters__group" role="group" aria-labelledby="mmv2-filter-slots">
            <span className="mmv2-filters__label" id="mmv2-filter-slots">
              Créneau
            </span>
            <div className="mmv2-chips mmv2-filters__chips">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot.value}
                  type="button"
                  className="mmv2-chip"
                  data-slot={slot.value}
                  aria-pressed={filters.slots.includes(slot.value)}
                  onClick={() => onToggleSlot(slot.value)}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {groupInfo}
      </div>
    </div>
  );
}
